import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import clsx from 'clsx';

interface CodeBlockProps {
  code: string;
  language?: string;
  title?: string;
}

export function CodeBlock({ code, language, title }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-fluent-lg overflow-hidden border border-surface-200 dark:border-white/[0.06]">
      {/* Header bar */}
      {(title || language) && (
        <div className="flex items-center justify-between px-4 py-2 bg-surface-100 dark:bg-white/[0.04] border-b border-surface-200 dark:border-white/[0.06]">
          <span className="text-xs text-surface-500 dark:text-surface-400 font-medium">
            {title || language}
          </span>
          <button
            onClick={handleCopy}
            className={clsx(
              'flex items-center gap-1.5 px-2 py-1 rounded-fluent text-xs transition-all',
              copied
                ? 'text-green-500'
                : 'text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-200 dark:hover:bg-white/[0.06]'
            )}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}
      {/* Code content */}
      <div className="relative group">
        <pre className="p-4 overflow-x-auto text-[13px] leading-relaxed bg-surface-50 dark:bg-[#0d0d0d]">
          <code className="font-mono text-surface-800 dark:text-surface-200 whitespace-pre">
            {code}
          </code>
        </pre>
        {/* Floating copy button when no header */}
        {!title && !language && (
          <button
            onClick={handleCopy}
            className={clsx(
              'absolute top-2 right-2 p-1.5 rounded-fluent transition-all',
              'opacity-0 group-hover:opacity-100',
              copied
                ? 'text-green-500 opacity-100'
                : 'text-surface-400 hover:text-surface-200 hover:bg-white/[0.06]'
            )}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
